import Command from "components/DesignSystem/Command/Command";
import { Button } from "components/DesignSystem/Button/Button";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/DesignSystem/Loader/loader";
import { FiCross } from "components/icons/FiCross";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useLazyGetGroupsListQuery } from "store/apis/crm";
import { GroupResponse } from "types/Models/groups";

type CompanyResultsProps = {
  searchTerm: string;
  recentCompanies: GroupResponse[];
  onSelectCompany: (company: GroupResponse) => void;
  onRemoveRecentCompany: (companyId: string) => void;
};

type CompanyItemProps = {
  company: GroupResponse;
  value: string;
  onSelectCompany: (company: GroupResponse) => void;
};

type RecentCompanyItemProps = {
  company: GroupResponse;
  onSelectCompany: (company: GroupResponse) => void;
  onRemoveRecentCompany: (companyId: string) => void;
};

const LoadingMore = () => (
  <div className="t-flex t-justify-center t-items-center t-w-full t-gap-2 t-p-2">
    <span className="t-flex t-origin-center t-animate-spin">
      <LoadingIcon />
    </span>
    Loading...
  </div>
);

const CompanyItem = ({ company, value, onSelectCompany }: CompanyItemProps) => {
  const handleSelect = () => onSelectCompany(company);

  return (
    <Command.Item value={value} onSelect={handleSelect}>
      {company.name}
    </Command.Item>
  );
};

const RecentCompanyItem = ({
  company,
  onSelectCompany,
  onRemoveRecentCompany,
}: RecentCompanyItemProps) => {
  const handleSelect = () => onSelectCompany(company);

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemoveRecentCompany(company.uuid);
  };

  const removeButton = (
    <Button
      type="button"
      customType="ghost_icon"
      size="xxsmall"
      aria-label={`Remove ${company.name} from recent`}
      onClick={handleRemove}
      className="t-opacity-0 group-hover:t-opacity-100 group-data-[selected=true]:t-opacity-100 focus-visible:t-opacity-100"
    >
      <FiCross />
    </Button>
  );

  return (
    <Command.Item
      value={`recent-${company.uuid}`}
      onSelect={handleSelect}
      suffix={removeButton}
    >
      {company.name}
    </Command.Item>
  );
};

export const CompanyResults = ({
  searchTerm,
  recentCompanies,
  onSelectCompany,
  onRemoveRecentCompany,
}: CompanyResultsProps) => {
  const {
    data: groups,
    loadNext,
    isFetching,
    isLoading,
    isSuccess,
  } = usePaginatedQuery<{
    company_groups?: GroupResponse[];
  }>(useLazyGetGroupsListQuery, "company_groups", {
    search_term: searchTerm,
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const hasReachedBottom = scrollHeight - scrollTop <= clientHeight + 50;

    if (hasReachedBottom && !isFetching) {
      loadNext();
    }
  };

  const { company_groups = [] } = groups || {};
  const isEmpty = !isLoading && !isFetching && company_groups.length === 0;
  const shouldShowRecent = !searchTerm && recentCompanies.length > 0;
  const loadedCompanyById = new Map(
    company_groups.map((company) => [company.uuid, company])
  );
  const freshRecentCompanies = recentCompanies.map(
    (company) => loadedCompanyById.get(company.uuid) ?? company
  );

  return (
    <Command.List className="t-h-[30rem]" onScroll={handleScroll}>
      {shouldShowRecent && (
        <Command.Group heading="Recent">
          {freshRecentCompanies.map((company) => (
            <RecentCompanyItem
              key={company.uuid}
              company={company}
              onSelectCompany={onSelectCompany}
              onRemoveRecentCompany={onRemoveRecentCompany}
            />
          ))}
        </Command.Group>
      )}

      {shouldShowRecent && <Command.Separator alwaysRender />}

      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={isEmpty}
        renderOnLoad={
          <div className="t-flex t-justify-center t-p-4">
            <Loader customType="secondary" size="small" />
          </div>
        }
      >
        <Async.Empty>
          <div className="t-p-4 t-text-center t-text-body-sm t-text-text-30">
            No companies found
          </div>
        </Async.Empty>
        <Async.Success>
          <Command.Group
            heading={shouldShowRecent ? "All companies" : undefined}
          >
            {company_groups.map((company) => (
              <CompanyItem
                key={company.uuid}
                company={company}
                value={company.uuid}
                onSelectCompany={onSelectCompany}
              />
            ))}
          </Command.Group>
        </Async.Success>

        {isFetching && !isLoading && <LoadingMore />}
      </Async.Root>
    </Command.List>
  );
};
